import React from "react";
import PropTypes from "prop-types";
import { Box } from "@material-ui/core";
import { formatearFecha } from "src/utils/formatTime";
import { BRAND } from "src/theme/brandTokens";
import { formatearCaptionItem } from "src/utils/propuestaItemLabels";
import { formatValorItem } from "src/utils/propuestasV2/formatValorItem";

const FORMATO_FECHA_LARGA = "DD [de] MMMM [de] YYYY HH:mm";

function labelEstado(estado) {
  if (estado === "ACEPTADA") return "Aceptada";
  if (estado === "PARCIALMENTE_ACEPTADA") return "Parcialmente aceptada";
  if (estado === "RECHAZADA") return "Rechazada";
  return estado || "—";
}

const Eyebrow = ({ children }) => (
  <Box
    sx={{
      fontSize: BRAND.font.size.eyebrow,
      color: BRAND.color.grey50,
      textTransform: "uppercase",
      letterSpacing: BRAND.font.letterSpacing.widest,
      fontWeight: BRAND.font.weight.medium,
      mb: 1,
    }}
  >
    {children}
  </Box>
);

const Valor = ({ children, secundario }) => (
  <Box
    sx={{
      fontSize: secundario ? BRAND.font.size.body : BRAND.font.size.bodyLarge,
      color: secundario ? BRAND.color.grey70 : BRAND.color.inkSoft,
      fontWeight: secundario
        ? BRAND.font.weight.regular
        : BRAND.font.weight.medium,
      lineHeight: BRAND.font.lineHeight.snug,
      mt: secundario ? 0.5 : 0,
    }}
  >
    {children}
  </Box>
);

const Divisor = () => (
  <Box
    component="hr"
    sx={{
      border: 0,
      borderTop: `1px solid ${BRAND.color.divider}`,
      m: 0,
      my: 4,
    }}
  />
);

const ItemRow = ({ numero, item, esRechazado }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      gap: 2,
      py: 1.5,
      borderBottom: `1px solid ${BRAND.color.divider}`,
      "&:last-child": { borderBottom: 0 },
      opacity: esRechazado ? 0.7 : 1,
    }}
  >
    <Box
      sx={{
        flexShrink: 0,
        width: "20pt",
        fontSize: BRAND.font.size.eyebrow,
        color: BRAND.color.grey50,
        fontWeight: BRAND.font.weight.bold,
        letterSpacing: BRAND.font.letterSpacing.widest,
        pt: "3pt",
      }}
    >
      {String(numero).padStart(2, "0")}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box
        sx={{
          fontSize: BRAND.font.size.body,
          fontWeight: BRAND.font.weight.medium,
          color: BRAND.color.inkSoft,
          lineHeight: BRAND.font.lineHeight.snug,
        }}
      >
        {item.nombreServicio || `Ítem ${item.id}`}
      </Box>
      {!esRechazado && item.tipoItem && (
        <Box
          sx={{
            fontSize: BRAND.font.size.bodySmall,
            fontWeight: BRAND.font.weight.light,
            color: BRAND.color.grey70,
            mt: 0.5,
          }}
        >
          {formatearCaptionItem(item)}
        </Box>
      )}
      {esRechazado && item.motivo && (
        <Box
          sx={{
            fontSize: BRAND.font.size.bodySmall,
            color: BRAND.color.grey70,
            fontStyle: "italic",
            mt: 0.5,
            lineHeight: BRAND.font.lineHeight.body,
          }}
        >
          {item.motivo}
        </Box>
      )}
    </Box>
    {!esRechazado && (
      <Box
        sx={{
          flexShrink: 0,
          fontSize: BRAND.font.size.body,
          fontWeight: BRAND.font.weight.bold,
          color: BRAND.color.inkSoft,
          textAlign: "right",
          whiteSpace: "nowrap",
          pt: "3pt",
        }}
      >
        {formatValorItem(item.tipoMoneda, item.valorConDescuento)}
      </Box>
    )}
  </Box>
);

/**
 * Render V2 — verificación de QR del portal V2. Shape multi-empresa con
 * items aceptados/rechazados explícitos y motivos de rechazo.
 */
const DetallesV2 = ({ data }) => {
  const d = data.detalles || {};
  const itemsAceptados = data.itemsAceptados || [];
  const itemsRechazados = data.itemsRechazados || [];

  // Agrupación de aceptados por empresa
  const gruposAceptados = (() => {
    const map = new Map();
    for (const it of itemsAceptados) {
      const key = it.empresa?.rut || "__sin_empresa__";
      if (!map.has(key)) {
        map.set(key, { empresa: it.empresa || null, items: [] });
      }
      map.get(key).items.push(it);
    }
    return Array.from(map.values());
  })();

  return (
    <Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 4,
        }}
      >
        <Box>
          <Eyebrow>Solicitante</Eyebrow>
          <Valor>{data.solicitante?.nombre || "—"}</Valor>
          {data.solicitante?.rut && (
            <Valor secundario>RUT {data.solicitante.rut}</Valor>
          )}
        </Box>
        <Box>
          <Eyebrow>Estado de la respuesta</Eyebrow>
          <Valor>{labelEstado(data.estado)}</Valor>
        </Box>
        <Box>
          <Eyebrow>Aceptada por</Eyebrow>
          <Valor>{data.datosAceptante || "—"}</Valor>
          {data.ipFirma && <Valor secundario>IP {data.ipFirma}</Valor>}
        </Box>
        <Box>
          <Eyebrow>Método de verificación</Eyebrow>
          <Valor>{d.tipoVerificacion || "OTP"}</Valor>
        </Box>
      </Box>

      {itemsAceptados.length > 0 && (
        <>
          <Divisor />
          <Box>
            <Eyebrow>Servicios aceptados · {itemsAceptados.length}</Eyebrow>
            {gruposAceptados.map((g, gi) => (
              <Box key={gi} sx={{ mt: gi > 0 ? 4 : 2 }}>
                {g.empresa && (
                  <Box
                    sx={{
                      fontFamily: BRAND.font.family.display,
                      fontSize: BRAND.font.size.body,
                      fontWeight: BRAND.font.weight.bold,
                      color: BRAND.color.inkSoft,
                      mb: 1.5,
                    }}
                  >
                    {g.empresa.nombre}
                    {g.empresa.rut && (
                      <Box
                        component="span"
                        sx={{
                          ml: 1,
                          fontWeight: BRAND.font.weight.regular,
                          fontSize: BRAND.font.size.bodySmall,
                          color: BRAND.color.grey70,
                        }}
                      >
                        · {g.empresa.rut}
                      </Box>
                    )}
                  </Box>
                )}
                {g.items.map((it, idx) => (
                  <ItemRow key={it.id ?? idx} numero={idx + 1} item={it} />
                ))}
              </Box>
            ))}
          </Box>
        </>
      )}

      {itemsRechazados.length > 0 && (
        <>
          <Divisor />
          <Box>
            <Eyebrow>Servicios rechazados · {itemsRechazados.length}</Eyebrow>
            <Box sx={{ mt: 2 }}>
              {itemsRechazados.map((it, idx) => (
                <ItemRow
                  key={it.id ?? idx}
                  numero={idx + 1}
                  item={it}
                  esRechazado
                />
              ))}
            </Box>
          </Box>
        </>
      )}

      <Divisor />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 4,
        }}
      >
        <Box>
          <Eyebrow>Fecha de firma</Eyebrow>
          <Valor>{formatearFecha(data.fechaFirma, FORMATO_FECHA_LARGA)}</Valor>
          {typeof d.diasTranscurridos === "number" && (
            <Valor secundario>
              {d.diasTranscurridos === 0
                ? "Hoy"
                : `Hace ${d.diasTranscurridos} día${d.diasTranscurridos === 1 ? "" : "s"}`}
            </Valor>
          )}
        </Box>
        <Box>
          <Eyebrow>Fecha de verificación</Eyebrow>
          <Valor>
            {formatearFecha(
              d.fechaVerificacion || new Date().toISOString(),
              FORMATO_FECHA_LARGA,
            )}
          </Valor>
        </Box>
      </Box>
    </Box>
  );
};

Eyebrow.propTypes = { children: PropTypes.node.isRequired };
Valor.propTypes = {
  children: PropTypes.node.isRequired,
  secundario: PropTypes.bool,
};
ItemRow.propTypes = {
  numero: PropTypes.number.isRequired,
  item: PropTypes.object.isRequired,
  esRechazado: PropTypes.bool,
};

DetallesV2.propTypes = {
  data: PropTypes.object.isRequired,
};

export default DetallesV2;
